import { importSkillsFromPackage, listInstalledSkills } from './importer.js';
import { theme } from '../theme.js';

interface ParsedAddArgs {
  packageRef: string;
  skills: string[];
  scope: 'user' | 'project';
  force: boolean;
}

function helpLines(): string[] {
  return [
    theme.bold(theme.primary('Import skills from skills.sh')),
    `  ${theme.primary('1.')} Browse ${theme.primaryLight('https://skills.sh')} and copy the package shown as ${theme.primaryLight('owner/repo')}.`,
    '  2. Import every skill in a package:',
    `     ${theme.primaryLight('max skills add <owner/repo>')}`,
    '  3. Import one skill from a package:',
    `     ${theme.primaryLight('max skills add <owner/repo> --skill <skill-name>')}`,
    '  4. List installed skills:',
    `     ${theme.primaryLight('max skills list')}`,
    '',
    theme.bold(theme.primary('Options')),
    `  ${theme.warning('--skill, -s')}     Import only the named skill. Repeat or comma-separate names.`,
    `  ${theme.warning('--project, -p')}   Install into this repo at ${theme.primaryLight('.max/skills')} instead of ${theme.primaryLight('~/.max/skills')}.`,
    `  ${theme.warning('--global, -g')}    Install into ${theme.primaryLight('~/.max/skills')} (default).`,
    `  ${theme.warning('--force, -f')}     Replace an already installed skill.`,
    '',
    theme.bold(theme.primary('Examples')),
    `  ${theme.primaryLight('max skills add mattpocock/skills --skill tdd')}`,
    `  ${theme.primaryLight('max skills add vercel-labs/agent-skills')}`,
    `  ${theme.primaryLight('max skills add anthropics/skills --project')}`,
    '',
    'Inside Max, use the same commands with a slash:',
    `  ${theme.primaryLight('/skills add mattpocock/skills --skill tdd')}`,
  ];
}

function importHintLines(): string[] {
  return [
    '',
    theme.bold(theme.primary('Import more from skills.sh:')),
    `  ${theme.primary('1.')} Browse ${theme.primaryLight('https://skills.sh')} and copy the package shown as ${theme.primaryLight('owner/repo')}.`,
    `  ${theme.primary('2.')} Import all skills:  ${theme.primaryLight('/skills add <owner/repo>')}`,
    `  ${theme.primary('3.')} Import one skill:   ${theme.primaryLight('/skills add <owner/repo> --skill <skill-name>')}`,
    `  ${theme.muted('Terminal form:')}         ${theme.primaryLight('max skills add <owner/repo> --skill <skill-name>')}`,
    `  ${theme.muted('More help:')}             ${theme.primaryLight('/skills help')}`,
  ];
}

function formatSkillLine(skill: ReturnType<typeof listInstalledSkills>[number]): string {
  return `  ${theme.bold(theme.primaryLight(skill.name))} ${theme.muted(`(${skill.source})`)} ${theme.muted('-')} ${skill.description}`;
}

function parseAddArgs(args: string[]): ParsedAddArgs {
  const packageRef = args[0];
  if (!packageRef || packageRef.startsWith('-')) {
    throw new Error('Usage: max skills add <owner/repo> [--skill name] [--project] [--force]');
  }

  const skills: string[] = [];
  let scope: 'user' | 'project' = 'user';
  let force = false;

  for (let i = 1; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--project' || arg === '-p') {
      scope = 'project';
    } else if (arg === '--global' || arg === '-g') {
      scope = 'user';
    } else if (arg === '--force' || arg === '-f') {
      force = true;
    } else if (arg === '--skill' || arg === '-s') {
      const value = args[++i];
      if (!value || value.startsWith('-')) {
        throw new Error('--skill requires a skill name.');
      }
      skills.push(...value.split(',').map((item) => item.trim()).filter(Boolean));
    } else {
      throw new Error(`Unknown skills add option: ${arg}`);
    }
  }

  return { packageRef, skills, scope, force };
}

export async function runSkillsCommand(args: string[]): Promise<string[]> {
  const command = args[0] ?? 'list';

  if (command === 'help' || command === '--help' || command === '-h') {
    return helpLines();
  }

  if (command === 'list' || command === 'ls') {
    const skills = listInstalledSkills();
    if (skills.length === 0) return [theme.muted('No skills installed.'), ...importHintLines()];
    return [
      theme.bold(theme.primary('Available skills:')),
      ...skills.map(formatSkillLine),
      ...importHintLines(),
    ];
  }

  if (command === 'add' || command === 'import') {
    const parsed = parseAddArgs(args.slice(1));
    const result = await importSkillsFromPackage({
      packageRef: parsed.packageRef,
      skills: parsed.skills,
      scope: parsed.scope,
      force: parsed.force,
    });

    const lines = [`${theme.success('Installed to')} ${theme.primaryLight(result.destinationRoot)}`];
    if (result.imported.length > 0) {
      lines.push(
        ...result.imported.map(
          (skill) => `  ${theme.success('+')} ${theme.bold(theme.primaryLight(skill.name))} ${theme.muted(`(${skill.files} files)`)}`,
        ),
      );
    }
    if (result.skipped.length > 0) {
      lines.push(...result.skipped.map((skill) => `  ${theme.warning('skipped:')} ${skill}`));
    }
    if (result.imported.length === 0) {
      lines.push(theme.muted('No new skills imported.'));
    }
    return lines;
  }

  throw new Error(`Unknown skills command: ${command}`);
}
